import styled from "@emotion/styled";
import { IonContent } from "@ionic/react";
import type { FC } from "react";
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import Path from "src/constants/path";

interface NoticeEntry {
  name: string;
  version: string;
  license: string;
  url: string | null;
  sourceUrl: string | null;
  licenseText: string | null;
  noticeText: string | null;
  lgplNotice: string | null;
}

interface OssLicensesDoc {
  generatedAt: string;
  app: string;
  lgpl: NoticeEntry[];
  other: NoticeEntry[];
  gpl3Text: string | null;
}

// Manifest is emitted by scripts/vite-plugin-license-notices.ts during
// production builds and served from the deployed origin. In dev it
// doesn't exist; we render an empty-state. Resolved against the Vite
// base URL so preview builds served from a subpath (e.g.
// `/preview-feature-foo/`) still locate the manifest correctly.
// Fully-qualified against window.location.origin because Capacitor's
// fetch shim runs the request URL through `new URL(...)`, which throws
// on root-relative paths.
const MANIFEST_PATH = new URL(
  `${import.meta.env.BASE_URL.replace(/\/$/, "")}/oss-licenses.json`,
  window.location.origin
).toString();

// Group permissively-licensed deps by their SPDX license string, ordered by
// group size (most common license first). Dedupes name+license so multiple
// versions of the same package collapse to a single entry.
const groupByLicense = (entries: NoticeEntry[]): [string, NoticeEntry[]][] => {
  const groups = new Map<string, Map<string, NoticeEntry>>();
  for (const e of entries) {
    const license = e.license || "Unknown";
    let byName = groups.get(license);
    if (!byName) {
      byName = new Map();
      groups.set(license, byName);
    }
    if (!byName.has(e.name)) {
      byName.set(e.name, e);
    }
  }
  return [...groups.entries()]
    .map(([license, byName]) => {
      const list = [...byName.values()].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      return [license, list] as [string, NoticeEntry[]];
    })
    .sort((a, b) => b[1].length - a[1].length);
};

const Wrap = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: 2rem 1.25rem 4rem;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const BackLink = styled(RouterLink)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
  color: inherit;
  text-decoration: none;

  &::before {
    content: "\\2190";
    font-size: 1rem;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const Section = styled.section`
  margin-top: 2.5rem;
`;

const LicenseGroup = styled.div`
  margin-top: 1.25rem;

  h3 {
    margin: 0 0 0.35rem;
    font-size: 0.9rem;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.7);
    word-break: break-word;
  }

  a {
    color: inherit;
  }
`;

const Entry = styled.article`
  padding: 1rem 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);

  h3 {
    margin: 0 0 0.25rem;
    font-size: 1rem;
  }

  .meta {
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }

  pre {
    white-space: pre-wrap;
    word-break: break-word;
    background: rgba(0, 0, 0, 0.04);
    padding: 0.75rem;
    border-radius: 4px;
    font-size: 0.8rem;
    max-height: 320px;
    overflow: auto;
  }
`;

const Collapsible = styled.details`
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 0.5rem 0;

  > summary {
    cursor: pointer;
    list-style: none;
    font-size: 0.85rem;
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
  }

  > summary::-webkit-details-marker {
    display: none;
  }

  > summary::before {
    content: "\\25B8";
    font-size: 0.7rem;
    color: rgba(0, 0, 0, 0.5);
    transition: transform 120ms;
  }

  &[open] > summary::before {
    transform: rotate(90deg);
  }

  .name {
    font-weight: 500;
  }

  .meta {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.8rem;
  }

  pre {
    white-space: pre-wrap;
    word-break: break-word;
    background: rgba(0, 0, 0, 0.04);
    padding: 0.75rem;
    border-radius: 4px;
    font-size: 0.75rem;
    margin: 0.5rem 0 0;
    max-height: 360px;
    overflow: auto;
  }

  .notice-label {
    margin-top: 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.65);
  }
`;

const Missing = styled.div`
  margin: 0.5rem 0 0;
  padding: 0.5rem 0.75rem;
  background: rgba(220, 100, 0, 0.08);
  border-radius: 4px;
  font-size: 0.75rem;
  color: rgba(120, 60, 0, 0.95);
`;

const renderEntry = (e: NoticeEntry): React.ReactElement => (
  <Entry key={`${e.name}@${e.version}`}>
    <h3>
      {e.name} <span className="meta">v{e.version}</span>
    </h3>
    <div className="meta">
      License: {e.license}
      {e.url ? (
        <>
          {" · "}
          <a href={e.url} target="_blank" rel="noopener noreferrer">
            {e.url}
          </a>
        </>
      ) : null}
    </div>
    {e.lgplNotice ? <p>{e.lgplNotice}</p> : null}
    {e.licenseText ? <pre>{e.licenseText}</pre> : null}
    {e.noticeText ? (
      <>
        <div className="meta">NOTICE</div>
        <pre>{e.noticeText}</pre>
      </>
    ) : null}
  </Entry>
);

const renderCollapsibleEntry = (e: NoticeEntry): React.ReactElement => (
  <Collapsible key={`${e.name}@${e.version}`}>
    <summary>
      <span className="name">
        {e.url ? (
          <a
            href={e.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(ev) => ev.stopPropagation()}
          >
            {e.name}
          </a>
        ) : (
          e.name
        )}
      </span>
      <span className="meta">
        v{e.version} · {e.license}
      </span>
    </summary>
    {e.licenseText ? (
      <pre>{e.licenseText}</pre>
    ) : (
      <Missing>
        No LICENSE file shipped by this package. Refer to the project homepage
        for license terms.
      </Missing>
    )}
    {e.noticeText ? (
      <>
        <div className="notice-label">NOTICE</div>
        <pre>{e.noticeText}</pre>
      </>
    ) : null}
  </Collapsible>
);

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; doc: OssLicensesDoc }
  | { kind: "missing" }
  | { kind: "error"; message: string };

const OpenSourceLicensesPage: FC = () => {
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(MANIFEST_PATH, { cache: "no-cache" });
        if (!res.ok) {
          if (!cancelled) {
            setState({ kind: "missing" });
          }
          return;
        }
        const doc = (await res.json()) as OssLicensesDoc;
        if (!cancelled) {
          setState({ kind: "ready", doc });
        }
      } catch (e) {
        if (!cancelled) {
          setState({ kind: "error", message: (e as Error).message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <IonContent>
      <Wrap>
        <BackLink to={`/${Path.app}/${Path.home}`}>Back to app</BackLink>
        <h1>Open Source Licenses</h1>
        <p>
          This application uses open-source software. The libraries below are
          included in the app along with their licenses and copyright notices.
        </p>

        {state.kind === "loading" ? <p>Loading…</p> : null}
        {state.kind === "missing" ? (
          <p>
            License manifest is unavailable. This page is only populated in
            production builds.
          </p>
        ) : null}
        {state.kind === "error" ? (
          <p>Failed to load license manifest: {state.message}</p>
        ) : null}

        {state.kind === "ready" && state.doc.lgpl.length > 0 ? (
          <Section>
            <h2>LGPL / Copyleft Components</h2>
            <p>
              The following libraries are distributed under the GNU Lesser
              General Public License. They are shipped as separate JavaScript
              chunks and may be replaced by users with their own modified
              builds.
            </p>
            {state.doc.lgpl.map(renderEntry)}
            {state.doc.gpl3Text ? (
              <Collapsible>
                <summary>
                  <span className="name">GNU General Public License v3.0</span>
                  <span className="meta">accompanying LGPL-3.0 per §4(b)</span>
                </summary>
                <pre>{state.doc.gpl3Text}</pre>
              </Collapsible>
            ) : null}
          </Section>
        ) : null}

        {state.kind === "ready" ? (
          <Section>
            <h2>Other Components</h2>
            <p>
              This application also includes the libraries listed below. Each
              entry reproduces the upstream LICENSE (and NOTICE, if any) as
              required by the MIT, BSD, Apache-2.0, ISC and similar attribution
              clauses. Click a name to expand its license text.
            </p>
            {groupByLicense(state.doc.other).map(([license, entries]) => (
              <LicenseGroup key={license}>
                <h3>
                  {license}{" "}
                  <span style={{ fontWeight: 400, opacity: 0.6 }}>
                    ({entries.length})
                  </span>
                </h3>
                {entries.map(renderCollapsibleEntry)}
              </LicenseGroup>
            ))}
          </Section>
        ) : null}

        {state.kind === "ready" ? (
          <p
            className="meta"
            style={{ marginTop: "2rem", fontSize: "0.75rem" }}
          >
            Generated {state.doc.generatedAt}
          </p>
        ) : null}
      </Wrap>
    </IonContent>
  );
};

export default OpenSourceLicensesPage;
